import { supabase, applyOrgFilter, getActiveOrgOverride, DEFAULT_ORG_ID, signImagesDeep } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { computeEntregableEstatus, umbralHastaOrden } from './pagosEntregables'
import { deportesDeJugador } from '../deportesJugador'
import { normalizeSportValue } from '../../constants/sports'
import { puedeEntregar, numeroEfectivo, nicknameEfectivo } from '../uniformesReglas'
import { construirNecesidad, entregaDesdeCelda } from '../uniformesNecesidad'
import { planearCobro } from '../uniformesCobro'
import { getTemporadaPrendas } from './temporadaPrendas'
import { getJugadoresInscritos } from './temporadaInscripciones'

// =====================================================
// PRENDAS (catalogo)
// =====================================================

export const getPrendas = async (orgId = null) => {
  try {
    let query = supabase
      .from('prendas')
      .select('*, pagos_conceptos:concepto_id(id, nombre, tipo, monto_default)')
      .eq('activo', true)
      .order('deporte')
      .order('nombre')

    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPrendas]', error)
    return { data: [], error }
  }
}

export const createPrenda = async (prendaData) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // 1. Auto-crear concepto de pago tipo 'uniforme'
    const { data: concepto, error: conceptoError } = await supabase
      .from('pagos_conceptos')
      .insert({
        org_id: orgId,
        nombre: prendaData.nombre,
        tipo: 'uniforme',
        monto_default: prendaData.precio || null,
        descripcion: `Prenda: ${prendaData.nombre}${prendaData.deporte !== 'todos' ? ` (${prendaData.deporte})` : ''}`,
      })
      .select()
      .single()

    if (conceptoError) throw conceptoError

    // 2. Crear prenda vinculada al concepto
    const { data, error } = await supabase
      .from('prendas')
      .insert({
        org_id: orgId,
        nombre: prendaData.nombre,
        deporte: prendaData.deporte || 'todos',
        tallas: prendaData.tallas || ['S', 'M', 'L', 'XL'],
        precio: prendaData.precio || 0,
        concepto_id: concepto.id,
        descripcion: prendaData.descripcion || null,
        personalizada: prendaData.personalizada ?? false,
      })
      .select('*, pagos_conceptos:concepto_id(id, nombre, tipo)')
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createPrenda]', error)
    return { data: null, error }
  }
}

export const updatePrenda = async (id, updates) => {
  try {
    // Si cambia nombre o precio, actualizar el concepto vinculado
    if (updates.nombre || updates.precio !== undefined) {
      const { data: prenda } = await supabase
        .from('prendas')
        .select('concepto_id')
        .eq('id', id)
        .single()

      if (prenda?.concepto_id) {
        const conceptoUpdates = {}
        if (updates.nombre) conceptoUpdates.nombre = updates.nombre
        if (updates.precio !== undefined) conceptoUpdates.monto_default = updates.precio
        if (updates.nombre) {
          conceptoUpdates.descripcion = `Prenda: ${updates.nombre}${updates.deporte && updates.deporte !== 'todos' ? ` (${updates.deporte})` : ''}`
        }

        await supabase
          .from('pagos_conceptos')
          .update(conceptoUpdates)
          .eq('id', prenda.concepto_id)
      }
    }

    const { data, error } = await supabase
      .from('prendas')
      .update(updates)
      .eq('id', id)
      .select('*, pagos_conceptos:concepto_id(id, nombre, tipo)')
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePrenda]', error)
    return { data: null, error }
  }
}

export const deletePrenda = async (id) => {
  try {
    // Verificar entregas pendientes
    const { data: pendientes } = await supabase
      .from('jugadores_prendas')
      .select('id')
      .eq('prenda_id', id)
      .in('estatus', ['no_entregado', 'pendiente'])

    const { error } = await supabase
      .from('prendas')
      .update({ activo: false })
      .eq('id', id)

    if (error) throw error
    return { error: null, pendientes: pendientes?.length || 0 }
  } catch (error) {
    console.error('[deletePrenda]', error)
    return { error }
  }
}

// =====================================================
// JUGADORES_PRENDAS (asignacion + entrega)
// =====================================================

export const getJugadoresPrendas = async (temporadaId, orgId = null) => {
  try {
    let query = supabase
      .from('jugadores_prendas')
      .select(`
        *,
        jugadores:jugador_id(id, nombre_completo, numero, imagen_path, imagen_url, genero),
        prendas:prenda_id(id, nombre, deporte, tallas, precio)
      `)
      .eq('temporada_id', temporadaId)
      .order('created_at')

    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getJugadoresPrendas]', error)
    return { data: [], error }
  }
}

// Prendas asignadas a un jugador (con registro en jugadores_prendas) para la ficha.
// Devuelve origen (plan/extra), talla, estatus y pago. Si no se pasa temporadaId,
// usa la temporada abierta. No incluye prendas del plan aún sin talla (esas viven
// en la pantalla de Entregas); aquí se ve lo que ya tiene asignado el jugador.
export const getPrendasDeJugador = async (jugadorId, temporadaId = null) => {
  try {
    const orgId = getActiveOrgOverride() || null

    let tid = temporadaId
    if (!tid) {
      let tq = supabase.from('temporadas').select('id').eq('estado', 'abierta').order('fecha_inicio', { ascending: false }).limit(1)
      tq = applyOrgFilter(tq, orgId)
      const { data: temps } = await tq
      tid = temps?.[0]?.id || null
    }
    if (!tid) return { data: [], temporadaId: null, error: null }

    let query = supabase
      .from('jugadores_prendas')
      .select(`
        id, talla, estatus, origen, cantidad, pagado,
        prendas:prenda_id(id, nombre, deporte, precio),
        pagos_cargos:cargo_id(estatus)
      `)
      .eq('jugador_id', jugadorId)
      .eq('temporada_id', tid)
      .order('created_at')

    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error

    const rows = (data || []).map((r) => ({
      id: r.id,
      prenda_nombre: r.prendas?.nombre || 'Prenda',
      deporte: r.prendas?.deporte || null,
      origen: r.origen === 'extra' ? 'extra' : 'plan',
      cantidad: r.cantidad || 1,
      talla: r.talla || null,
      estatus: r.estatus || 'no_entregado',
      pagado: r.pagos_cargos ? r.pagos_cargos.estatus === 'pagado' : !!r.pagado,
      // `null` = no hay un cobro propio que consultar (su costo va dentro del plan).
      // NO es lo mismo que no haber pagado, y la ficha decia "No pagado" a todos.
      prendaPagada: r.pagos_cargos ? r.pagos_cargos.estatus === 'pagado' : null,
    }))
    return { data: rows, temporadaId: tid, error: null }
  } catch (error) {
    console.error('[getPrendasDeJugador]', error)
    return { data: [], temporadaId: null, error }
  }
}

// ¿El concepto (uniforme) ya está incluido en un plan ACTIVO asignado al jugador
// en esta temporada? Si sí, su costo ya va diluido en el plan (modelo fold-in) y NO
// debe generarse un cargo extra al registrar la entrega.
const conceptoEnPlanDelJugador = async (jugadorId, conceptoId, temporadaId) => {
  const { data: asignaciones } = await supabase
    .from('pagos_jugador_plan')
    .select('plan_id')
    .eq('jugador_id', jugadorId)
    .eq('temporada_id', temporadaId)
    .eq('activo', true)
  const planIds = (asignaciones || []).map((a) => a.plan_id)
  if (!planIds.length) return false
  const { data: items } = await supabase
    .from('pagos_planes_items')
    .select('id')
    .in('plan_id', planIds)
    .eq('concepto_id', conceptoId)
    .limit(1)
  return (items || []).length > 0
}

// Registra la entrega de una prenda a un jugador.
// origen: 'plan' = parte de su plan (sin cargo, se paga con el plan) | 'extra' = adicional
// (con su propio cargo = cantidad × precio). Si no se pasa origen, se infiere por
// compatibilidad (concepto en el plan del jugador → 'plan'; si no → 'extra').
export const asignarPrenda = async ({ jugadorId, prendaId, talla, temporadaId, cantidad = 1, origen = null, estatus = 'no_entregado', grupoId = null }) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
    const qty = Math.max(1, Math.floor(Number(cantidad) || 1))

    const { data: prenda } = await supabase
      .from('prendas')
      .select('concepto_id, precio')
      .eq('id', prendaId)
      .single()

    let resolvedOrigen = origen
    if (!resolvedOrigen && prenda?.concepto_id) {
      resolvedOrigen = (await conceptoEnPlanDelJugador(jugadorId, prenda.concepto_id, temporadaId)) ? 'plan' : 'extra'
    }
    resolvedOrigen = resolvedOrigen || 'extra'

    // IDEMPOTENTE para el plan. `uq_jp_plan_unico` es
    // (jugador_id, prenda_id, temporada_id) WHERE origen='plan': solo puede haber UNA
    // fila de plan. Las pantallas deciden insertar o actualizar mirando el estado que
    // tienen en memoria, y ese solo se refresca al terminar la recarga -- capturar dos
    // tallas seguidas en el mismo renglon reventaba con "duplicate key value violates
    // unique constraint". El invariante es de la base, asi que se respeta aqui y no
    // en cada pantalla.
    //
    // Los EXTRAS no entran: el indice es parcial y a proposito se pueden repetir.
    if (resolvedOrigen === 'plan') {
      let existenteQuery = supabase
        .from('jugadores_prendas')
        .select('id')
        .eq('jugador_id', jugadorId)
        .eq('prenda_id', prendaId)
        .eq('temporada_id', temporadaId)
        .eq('origen', 'plan')
        .limit(1)
      existenteQuery = applyOrgFilter(existenteQuery, orgId)
      const { data: existentes } = await existenteQuery
      const existente = Array.isArray(existentes) ? existentes[0] : null

      if (existente) {
        const cambios = { talla, estatus }
        if (grupoId) cambios.grupo_id = grupoId
        let updateQuery = supabase.from('jugadores_prendas').update(cambios).eq('id', existente.id)
        updateQuery = applyOrgFilter(updateQuery, orgId)
        const { error: updErr } = await updateQuery
        if (updErr) throw updErr
        return { data: { id: existente.id, ...cambios, origen: 'plan' }, error: null }
      }
    }

    // Solo los EXTRAS generan cargo, y CADA extra crea el suyo (= cantidad × precio).
    // Los del plan se pagan con el plan (sin cargo propio).
    let cargoId = null
    if (resolvedOrigen === 'extra' && prenda?.concepto_id && prenda.precio > 0) {
      const monto = Math.round(prenda.precio * qty * 100) / 100
      const { data: cargo } = await supabase
        .from('pagos_cargos')
        .insert({
          org_id: orgId,
          temporada_id: temporadaId,
          jugador_id: jugadorId,
          concepto_id: prenda.concepto_id,
          monto_original: monto,
          monto_final: monto,
          monto_pagado: 0,
          saldo: monto,
          estatus: 'pendiente',
          notas: `Prenda extra (x${qty}, fuera de plan)`,
        })
        .select()
        .single()
      cargoId = cargo?.id
    }

    const { data, error } = await supabase
      .from('jugadores_prendas')
      .insert({
        org_id: orgId,
        jugador_id: jugadorId,
        prenda_id: prendaId,
        temporada_id: temporadaId,
        talla,
        cargo_id: cargoId,
        origen: resolvedOrigen,
        cantidad: qty,
        estatus,
        grupo_id: grupoId,
      })
      .select(`
        *,
        jugadores:jugador_id(id, nombre_completo, numero),
        prendas:prenda_id(id, nombre, deporte)
      `)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[asignarPrenda]', error)
    return { data: null, error }
  }
}

export const asignarPrendaMasivo = async ({ jugadorIds, prendaId, tallas, temporadaId, cantidad = 1, origen = 'extra' }) => {
  try {
    let count = 0
    for (let i = 0; i < jugadorIds.length; i++) {
      const talla = typeof tallas === 'string' ? tallas : (tallas[i] || tallas[0])
      const { error } = await asignarPrenda({
        jugadorId: jugadorIds[i],
        prendaId,
        talla,
        temporadaId,
        cantidad,
        origen,
      })
      if (!error) count++
    }
    return { count, error: null }
  } catch (error) {
    console.error('[asignarPrendaMasivo]', error)
    return { count: 0, error }
  }
}

export const actualizarAsignacion = async (id, updates) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
    const allowed = {}

    if (updates.talla !== undefined) allowed.talla = updates.talla
    if (updates.grupo_id !== undefined) allowed.grupo_id = updates.grupo_id
    if (updates.estatus !== undefined) {
      // Candado de entrega: la misma regla de tres casos que usa la UI
      // (puedeEntregar). Si cambia alla, tiene que cambiar aqui.
      if (updates.estatus === 'entregado') {
        const { data: actual } = await supabase
          .from('jugadores_prendas')
          .select(`
            id, talla, linea_id, recibido_at,
            prendas(personalizada),
            pedidos_uniforme_lineas(id, cantidad_pedida, cantidad_recibida,
                                    pedidos_uniforme(estado))
          `)
          .eq('id', id)
          .eq('org_id', orgId)
          .single()

        const linea = actual?.pedidos_uniforme_lineas || null
        const pedido = linea?.pedidos_uniforme || null
        let entregadosDeLinea = 0
        if (linea && actual?.prendas?.personalizada === false) {
          const { data: hermanos } = await supabase
            .from('jugadores_prendas')
            .select('cantidad, estatus')
            .eq('linea_id', linea.id)
            .eq('org_id', orgId)
          entregadosDeLinea = (hermanos || [])
            .filter((h) => h.estatus === 'entregado')
            .reduce((s, h) => s + (h.cantidad || 1), 0)
        }

        const ok = puedeEntregar(actual || {}, {
          pedido, linea, entregadosDeLinea,
          personalizada: actual?.prendas?.personalizada !== false,
        })
        if (!ok) {
          return { data: null, error: new Error('Esta prenda aun no se ha recibido del proveedor') }
        }
        allowed.entregado_at = new Date().toISOString()
        allowed.entregado_por = staff?.id
      }
      allowed.estatus = updates.estatus
    }
    if (updates.notas !== undefined) allowed.notas = updates.notas

    const { data, error } = await supabase
      .from('jugadores_prendas')
      .update(allowed)
      .eq('id', id)
      .eq('org_id', orgId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[actualizarAsignacion]', error)
    return { data: null, error }
  }
}

export const eliminarAsignacion = async (id) => {
  try {
    // Obtener cargo vinculado para cancelarlo
    const { data: asignacion } = await supabase
      .from('jugadores_prendas')
      .select('cargo_id, org_id, pedido_id, pedidos_uniforme:pedido_id(estado, proveedor)')
      .eq('id', id)
      .single()

    // Una pieza que ya se le mando al proveedor no se borra por detras: el documento
    // que el proveedor tiene en la mano dice otra cosa, y rehacer las lineas borraria
    // lo ya recibido. Hay que sacarla del pedido primero, con su propia accion.
    const pedido = asignacion?.pedidos_uniforme || null
    if (pedido && !['borrador', 'cancelado'].includes(pedido.estado)) {
      return {
        error: new Error(
          `Esta prenda va en el pedido a "${pedido.proveedor}", que ya está ${pedido.estado}. Quítala del pedido antes de eliminarla.`,
        ),
      }
    }

    // Cancelar cargo si existe y no esta pagado
    if (asignacion?.cargo_id) {
      await supabase
        .from('pagos_cargos')
        .update({ estatus: 'cancelado', notas: 'Prenda desasignada' })
        .eq('id', asignacion.cargo_id)
        .in('estatus', ['pendiente', 'vencido'])
    }

    const { error } = await supabase
      .from('jugadores_prendas')
      .delete()
      .eq('id', id)

    if (error) throw error

    // Las lineas se derivan de las piezas: si no se rehacen, la linea sigue pidiendo
    // una prenda que ya no le toca a nadie -- y eso es lo que ve el proveedor.
    if (asignacion?.pedido_id && asignacion?.org_id) {
      await recalcularLineas(asignacion.pedido_id, asignacion.org_id)
    }
    return { error: null }
  } catch (error) {
    console.error('[eliminarAsignacion]', error)
    return { error }
  }
}

// =====================================================
// MATRIZ DE ENTREGA
// =====================================================

export const getMatrizEntrega = async (temporadaId, { deporteFilter = null } = {}) => {
  try {
    const orgId = getActiveOrgOverride() || null

    // Obtener prendas activas
    let prendasQuery = supabase
      .from('prendas')
      .select('*')
      .eq('activo', true)
      .order('deporte')
      .order('nombre')

    prendasQuery = applyOrgFilter(prendasQuery, orgId)
    if (deporteFilter && deporteFilter !== 'todos') {
      prendasQuery = prendasQuery.or(`deporte.eq.${deporteFilter},deporte.eq.todos`)
    }

    const { data: prendas } = await prendasQuery

    // Mapa de concepto_id → prenda_id
    const conceptoToPrenda = {}
    ;(prendas || []).forEach((p) => {
      if (p.concepto_id) conceptoToPrenda[p.concepto_id] = p.id
    })
    const prendaPorId = Object.fromEntries((prendas || []).map((p) => [p.id, p]))

    // Obtener jugadores activos con sus deportes y grupos
    let jugadoresQuery = supabase
      .from('jugadores')
      .select(`
        id, nombre_completo, numero, imagen_path, imagen_url, genero,
        jugadores_meta(meta_key, meta_value),
        jugadores_grupos!left(grupo_id, numero_jersey, activo, deleted, grupos(id, nombre_del_grupo, deporte)),
        temporada_inscripciones!left(deporte_id, estatus, temporadas(estado))
      `)
      .eq('activo', true)
      .or('deleted.is.null,deleted.eq.false')
      .order('nombre_completo')

    jugadoresQuery = applyOrgFilter(jugadoresQuery, orgId)
    const { data: jugadores } = await jugadoresQuery
    await signImagesDeep(jugadores)

    // Obtener asignaciones de esta temporada con cargo vinculado
    let asignacionesQuery = supabase
      .from('jugadores_prendas')
      .select('*, pagos_cargos:cargo_id(id, estatus, monto_pagado, saldo)')
      .eq('temporada_id', temporadaId)

    asignacionesQuery = applyOrgFilter(asignacionesQuery, orgId)
    const { data: asignaciones } = await asignacionesQuery

    // El padron y las prendas de la temporada: de aqui sale la necesidad ahora.
    const [{ data: temporadaPrendas }, inscritos] = await Promise.all([
      getTemporadaPrendas(temporadaId),
      getJugadoresInscritos(temporadaId).catch(() => []),
    ])

    // Contexto de pedido y linea: sin esto la UI no puede distinguir "en pedido"
    // de "pedido al proveedor", ni saber si queda pieza libre de una generica.
    const pedidoIds = [...new Set((asignaciones || []).map((a) => a.pedido_id).filter(Boolean))]
    const lineaIds = [...new Set((asignaciones || []).map((a) => a.linea_id).filter(Boolean))]

    const pedidoPorId = {}
    if (pedidoIds.length) {
      let pedidosQuery = supabase
        .from('pedidos_uniforme').select('id, proveedor, estado, fecha_pedido').in('id', pedidoIds)
      pedidosQuery = applyOrgFilter(pedidosQuery, orgId)
      const { data: peds } = await pedidosQuery
      ;(peds || []).forEach((p) => { pedidoPorId[p.id] = p })
    }

    const lineaPorId = {}
    if (lineaIds.length) {
      let lineasQuery = supabase
        .from('pedidos_uniforme_lineas')
        .select('id, cantidad_pedida, cantidad_recibida').in('id', lineaIds)
      lineasQuery = applyOrgFilter(lineasQuery, orgId)
      const { data: lins } = await lineasQuery
      ;(lins || []).forEach((l) => { lineaPorId[l.id] = l })
    }

    // Cuantas piezas de cada linea ya se repartieron: es el consumo del pool de
    // las prendas genericas.
    const entregadosPorLinea = {}
    ;(asignaciones || []).forEach((a) => {
      if (!a.linea_id || a.estatus !== 'entregado') return
      entregadosPorLinea[a.linea_id] = (entregadosPorLinea[a.linea_id] || 0) + (a.cantidad || 1)
    })

    // Obtener cargos de pago de esta temporada para conceptos de prendas
    const conceptoIds = Object.keys(conceptoToPrenda)
    let cargosPlan = []
    if (conceptoIds.length > 0) {
      let cargosQuery = supabase
        .from('pagos_cargos')
        .select('id, jugador_id, concepto_id, plan_id, estatus, monto_final, monto_pagado, saldo')
        .eq('temporada_id', temporadaId)
        .in('concepto_id', conceptoIds)
        .neq('estatus', 'cancelado')

      cargosQuery = applyOrgFilter(cargosQuery, orgId)
      const { data } = await cargosQuery
      cargosPlan = data || []
    }

    // Mapa de cargos del plan: { jugadorId_prendaId: cargo }
    const cargosPlanMap = {}
    cargosPlan.forEach((c) => {
      const prendaId = conceptoToPrenda[c.concepto_id]
      if (prendaId) {
        cargosPlanMap[`${c.jugador_id}_${prendaId}`] = c
      }
    })

    // Fold-in: prendas del plan SIN cargo por-concepto (su costo va en el total del plan).
    // Se detectan por los items del plan asignado al jugador; el "pagado" se calcula por
    // el umbral del entregable (al llegar al Pago N) vs lo pagado por el jugador.
    let jpRows = []
    {
      let q = supabase.from('pagos_jugador_plan').select('jugador_id, plan_id')
        .eq('temporada_id', temporadaId).eq('activo', true)
      q = applyOrgFilter(q, orgId)
      const { data } = await q
      jpRows = data || []
    }
    const planesDelJugador = {} // jugadorId -> [plan_id]
    const planIdsSet = new Set()
    jpRows.forEach((r) => { (planesDelJugador[r.jugador_id] ||= []).push(r.plan_id); planIdsSet.add(r.plan_id) })

    const planConceptos = {}     // plan_id -> Set(concepto_id)
    const planParcialidades = {} // plan_id -> [{orden, monto}]
    const planEntregables = {}   // plan_id -> [{concepto_id, regla_tipo, hasta_orden}]
    if (planIdsSet.size > 0) {
      const ids = [...planIdsSet]
      const [itemsRes, parcsRes, entsRes] = await Promise.all([
        supabase.from('pagos_planes_items').select('plan_id, concepto_id').in('plan_id', ids),
        supabase.from('pagos_planes_parcialidades').select('id, plan_id, orden, monto').in('plan_id', ids),
        supabase.from('pagos_planes_entregables').select('plan_id, concepto_id, regla_tipo, hasta_orden').in('plan_id', ids),
      ])
      ids.forEach((pid) => { planConceptos[pid] = new Set(); planParcialidades[pid] = []; planEntregables[pid] = [] })
      ;(itemsRes.data || []).forEach((it) => { if (it.concepto_id) planConceptos[it.plan_id]?.add(it.concepto_id) })
      ;(parcsRes.data || []).forEach((p) => planParcialidades[p.plan_id]?.push(p))
      ;(entsRes.data || []).forEach((e) => planEntregables[e.plan_id]?.push(e))
    }

    // Pagado total por jugador en la temporada (cargos no cancelados), y sus
    // cargos por parcialidad: el umbral del entregable se mide contra lo que ESTE
    // jugador debe, no contra el precio de lista del plan.
    const pagadoTotalByJugador = {}
    const cargosPorJugadorParc = {}
    {
      let q = supabase.from('pagos_cargos').select('jugador_id, monto_pagado, monto_final, parcialidad_id')
        .eq('temporada_id', temporadaId).neq('estatus', 'cancelado')
      q = applyOrgFilter(q, orgId)
      const { data } = await q
      ;(data || []).forEach((c) => {
        pagadoTotalByJugador[c.jugador_id] = (pagadoTotalByJugador[c.jugador_id] || 0) + Number(c.monto_pagado || 0)
        if (!c.parcialidad_id) return
        ;(cargosPorJugadorParc[c.jugador_id] = cargosPorJugadorParc[c.jugador_id] || {})[c.parcialidad_id] = c
      })
    }

    // Construir mapa de asignaciones con estatus de pago real
    const asignacionMap = {}
    ;(asignaciones || []).forEach((a) => {
      const cargo = a.pagos_cargos
      asignacionMap[`${a.jugador_id}_${a.prenda_id}`] = {
        ...a,
        pagado: cargo ? cargo.estatus === 'pagado' : a.pagado,
      }
    })

    // Separar: prendas del plan vs extras
    // Del plan = hay cargo en cargosPlanMap para ese jugador+prenda
    // Extra = hay asignacion pero NO hay cargo del plan (fue creado al asignar)
    const planMap = {} // { jugadorId_prendaId: { asignacion, cargo } }
    const extrasMap = {} // { jugadorId_prendaId: asignacion }

    // Primero: poblar planMap desde cargos del plan
    Object.keys(cargosPlanMap).forEach((key) => {
      const cargo = cargosPlanMap[key]
      const asignacion = asignacionMap[key] || null
      planMap[key] = {
        ...(asignacion || {}),
        cargo,
        plan_id: cargo.plan_id,
        pagado: cargo.estatus === 'pagado',
        talla: asignacion?.talla || null,
        estatus: asignacion?.estatus || 'no_entregado',
        esPlan: true,
        necesitaTalla: !asignacion?.talla,
      }
    })

    // Segundo: prendas del plan en modelo fold-in (sin cargo por-concepto): el concepto
    // del uniforme está en los items de un plan del jugador. "pagado" por umbral del entregable.
    Object.keys(planesDelJugador).forEach((jugadorId) => {
      planesDelJugador[jugadorId].forEach((planId) => {
        const conceptos = planConceptos[planId]
        if (!conceptos) return
        conceptos.forEach((conceptoId) => {
          const prendaId = conceptoToPrenda[conceptoId]
          if (!prendaId) return // el concepto no es de una prenda
          const key = `${jugadorId}_${prendaId}`
          if (planMap[key]) return // ya cubierto por un cargo por-concepto (modelo viejo)
          const ent = (planEntregables[planId] || []).find((e) => e.concepto_id === conceptoId && e.regla_tipo === 'parcialidad')
          const parcs = planParcialidades[planId] || []
          const pagadoTotal = pagadoTotalByJugador[jugadorId] || 0
          const cargosParc = cargosPorJugadorParc[jugadorId] || {}
          let pagado
          if (ent && ent.hasta_orden != null) {
            pagado = computeEntregableEstatus(
              { regla_tipo: 'parcialidad', hasta_orden: ent.hasta_orden },
              { parcialidades: parcs, pagadoTotal, cargosPorParcialidadId: cargosParc },
            ).cubierto
          } else {
            const totalPlan = umbralHastaOrden(parcs, cargosParc)
            pagado = totalPlan > 0 && pagadoTotal >= totalPlan
          }
          const asignacion = asignacionMap[key] || null
          planMap[key] = {
            ...(asignacion || {}),
            cargo: null,
            plan_id: planId,
            pagado,
            talla: asignacion?.talla || null,
            estatus: asignacion?.estatus || 'no_entregado',
            esPlan: true,
            necesitaTalla: !asignacion?.talla,
          }
        })
      })
    })

    // Tercero: asignaciones que NO están en el plan = extras
    Object.keys(asignacionMap).forEach((key) => {
      if (!planMap[key]) {
        extrasMap[key] = {
          ...asignacionMap[key],
          esPlan: false,
        }
      }
    })

    // Filtrar jugadores por deporte si hay filtro
    let jugadoresFiltrados = jugadores || []
    if (deporteFilter && deporteFilter !== 'todos') {
      // La INSCRIPCION manda (spec §8.1): antes filtraba por el cache
      // jugadores_meta, que puede quedar viejo en silencio.
      jugadoresFiltrados = jugadoresFiltrados.filter((j) =>
        deportesDeJugador({
          inscripciones: j.temporada_inscripciones,
          jugadoresGrupos: j.jugadores_grupos,
        }).includes(normalizeSportValue(deporteFilter))
      )
    }

    // Jugadores que tienen al menos 1 entrada en planMap
    const jugadoresConPlan = new Set(
      Object.keys(planMap).map((k) => k.split('_')[0])
    )
    // Jugadores que tienen al menos 1 extra
    const jugadoresConExtras = new Set(
      Object.keys(extrasMap).map((k) => k.split('_')[0])
    )

    // Lista limpia de extras (origen='extra'), sin la colisión por clave jugador_prenda
    // (un jugador puede tener la misma prenda en plan Y como extra). El pago del extra
    // sale de su propio cargo.
    const extras = (asignaciones || [])
      .filter((a) => a.origen === 'extra')
      .map((a) => ({ ...a, pagado: a.pagos_cargos ? a.pagos_cargos.estatus === 'pagado' : false }))

    // Lista UNIFICADA de entregas: una entrada por prenda que le toca a cada jugador.
    //
    // La necesidad sale del PADRON x prendas de la temporada. Antes se recorria
    // planesDelJugador -> planConceptos -> conceptoToPrenda, y por eso una prenda
    // fuera de un plan no existia para el modulo: el becado al que le sacaron el
    // uniforme del plan quedaba invisible.
    //
    // VALVULA: si la temporada no tiene padron, se cae a los inscritos deducidos de
    // las asignaciones + los jugadores con plan, para no vaciar de golpe a un club
    // que aun no usa Inscripciones (mismo criterio que useInscritosTemporada).
    const inscritosEfectivos = (inscritos && inscritos.length)
      ? inscritos
      : [...new Set([
        ...Object.keys(planesDelJugador),
        ...(asignaciones || []).map((a) => a.jugador_id),
      ])]

    const celdas = construirNecesidad({
      inscritos: inscritosEfectivos,
      temporadaPrendas: temporadaPrendas || [],
      jugadores: jugadores || [],
      asignaciones: asignaciones || [],
    })

    const entregas = celdas.map((celda) => {
      const asig = (asignaciones || []).find((a) => a.id === celda.asignacionId) || {}
      const cargoPorConcepto = cargosPlanMap[`${celda.jugador_id}_${celda.prenda_id}`] || null
      const planId = (planesDelJugador[celda.jugador_id] || [])[0]
      const parcs = planParcialidades[planId] || []
      const pagadoTotal = pagadoTotalByJugador[celda.jugador_id] || 0
      const cargosParc = cargosPorJugadorParc[celda.jugador_id] || {}
      const conceptoId = Object.keys(conceptoToPrenda).find((c) => conceptoToPrenda[c] === celda.prenda_id)

      // ---- CANDADO 1: el pago del jersey. Decide QUE SE MANDA A HACER. ----
      // Cascada de tres pasos. `null` significa "no hay pago propio que consultar",
      // que NO es lo mismo que "no ha pagado".
      let prendaPagada = null
      if (celda.origen === 'extra') {
        prendaPagada = asig.pagos_cargos ? asig.pagos_cargos.estatus === 'pagado' : false
      } else if (cargoPorConcepto) {
        prendaPagada = cargoPorConcepto.estatus === 'pagado'
      } else if (conceptoId && parcs.some((p) => p.concepto_id === conceptoId)) {
        // La prenda tiene su PROPIA parcialidad con su propia fecha: esa es la
        // respuesta directa a "pago el jersey?". Lo calcula la regla 'concepto',
        // que ya existia y no se usaba.
        prendaPagada = computeEntregableEstatus(
          { regla_tipo: 'concepto', concepto_id: conceptoId },
          { parcialidades: parcs, pagadoTotal, cargosPorParcialidadId: cargosParc },
        ).cubierto
      }
      // Si no cayo en ninguna, queda null: el uniforme esta disuelto en el total del
      // plan (modelo fold-in) y no hay pago propio.
      //
      // AQUI VIVIA EL BUG: antes, este caso comparaba contra umbralHastaOrden(parcs,
      // cargosParc) SIN TOPE, o sea contra el plan COMPLETO. En PROD eso daba 0 de 37
      // jugadores "pagados" en el plan FLAG ($541 pagados en promedio de $6,000), asi
      // que nada podia mandarse a hacer nunca. Ese `else` se elimino a proposito.

      // ---- CANDADO 2: la mensualidad amarrada. Decide QUE SE ENTREGA. ----
      let ligadoAPago = null
      let condicionCumplida = null
      const ent = (planEntregables[planId] || []).find(
        (e) => e.concepto_id === conceptoId && e.regla_tipo === 'parcialidad',
      )
      if (ent && ent.hasta_orden != null) {
        ligadoAPago = ent.hasta_orden
        condicionCumplida = computeEntregableEstatus(
          { regla_tipo: 'parcialidad', hasta_orden: ent.hasta_orden },
          { parcialidades: parcs, pagadoTotal, cargosPorParcialidadId: cargosParc },
        ).cubierto
      }

      return entregaDesdeCelda(celda, {
        prendaPagada,
        ligadoAPago,
        condicionCumplida,
        prenda: prendaPorId[celda.prenda_id] || {},
        asig,
        pedido: pedidoPorId[asig.pedido_id] || null,
        linea: lineaPorId[asig.linea_id] || null,
        entregadosDeLinea: entregadosPorLinea[asig.linea_id] || 0,
      })
    })

    return {
      data: {
        prendas: prendas || [],
        // Las pantallas necesitan distinguir una prenda que va en la temporada de
        // una que solo aparece porque ya tenia asignaciones viejas.
        temporadaPrendas: temporadaPrendas || [],
        // Los pedidos que tocan a esta temporada. Repartir es por CAJA, y una caja es
        // un pedido: sin esto, la pantalla de reparto mostraba todas las piezas de la
        // temporada revueltas y habia que adivinar cuales venian en la que tienes
        // enfrente.
        pedidos: Object.values(pedidoPorId),
        jugadores: jugadoresFiltrados,
        planMap,
        extrasMap,
        extras,
        entregas,
        asignaciones: { ...planMap, ...extrasMap }, // backward compat
        jugadoresConPlan,
        jugadoresConExtras,
      },
      error: null,
    }
  } catch (error) {
    console.error('[getMatrizEntrega]', error)
    return { data: null, error }
  }
}

// =====================================================
// PEDIDOS DE UNIFORME
// =====================================================

export const getPedidos = async (orgId = null) => {
  try {
    let query = supabase
      .from('pedidos_uniforme')
      .select('*, lineas:pedidos_uniforme_lineas(id, prenda_id, talla, cantidad_pedida, cantidad_recibida)')
      .order('fecha_pedido', { ascending: false })
    query = applyOrgFilter(query, orgId)
    const { data, error } = await query
    if (error) throw error
    const rows = (data || []).map((p) => {
      const lineas = p.lineas || []
      return {
        ...p,
        lineas,
        items_count: lineas.length,
        cantidad_pedida: lineas.reduce((s, l) => s + (l.cantidad_pedida || 0), 0),
        cantidad_recibida: lineas.reduce((s, l) => s + (l.cantidad_recibida || 0), 0),
      }
    })
    return { data: rows, error: null }
  } catch (error) {
    console.error('[getPedidos]', error)
    return { data: [], error }
  }
}

// El pedido es de la org, no de la temporada de la pantalla: se consulta por
// pedido_id directo. Antes se deducia filtrando la matriz de la temporada activa,
// y por eso el detalle salia vacio al cambiar de temporada.
export const getPedido = async (pedidoId) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data: pedido, error: pErr } = await supabase
      .from('pedidos_uniforme')
      .select('*')
      .eq('id', pedidoId)
      .eq('org_id', orgId)
      .single()
    if (pErr) throw pErr

    const { data: lineas, error: lErr } = await supabase
      .from('pedidos_uniforme_lineas')
      .select('*, prendas(id, nombre, deporte, personalizada)')
      .eq('pedido_id', pedidoId)
      .eq('org_id', orgId)
    if (lErr) throw lErr

    const { data: items, error: iErr } = await supabase
      .from('jugadores_prendas')
      .select(`
        id, jugador_id, prenda_id, linea_id, talla, cantidad, origen, estatus,
        pagado, cargo_id, grupo_id, recibido_at, entregado_at,
        pagos_cargos:cargo_id(estatus),
        numero_impreso, nickname_impreso,
        jugadores(id, nombre_completo, numero, nickname,
                  jugadores_grupos(grupo_id, numero_jersey, nickname, activo, deleted)),
        prendas(id, nombre, deporte, personalizada)
      `)
      .eq('pedido_id', pedidoId)
      .eq('org_id', orgId)
    if (iErr) throw iErr

    // El pago de CADA pieza sale de su cargo, no de la columna `pagado` de
    // jugadores_prendas -- esa nadie la mantiene y decia "No pagado" a todo el mundo.
    // `null` = no hay un cobro propio enlazado que consultar, que NO es lo mismo que
    // no haber pagado.
    const itemsConPago = (items || []).map((it) => ({
      ...it,
      prendaPagada: it.pagos_cargos ? it.pagos_cargos.estatus === 'pagado' : null,
    }))

    return { data: { pedido, lineas: lineas || [], items: itemsConPago }, error: null }
  } catch (error) {
    console.error('[getPedido]', error)
    return { data: null, error }
  }
}

// Las lineas se derivan de los items del pedido: una por prenda+talla, con la
// suma de cantidades. Solo se recalculan en borrador, donde cantidad_recibida
// siempre es 0 y no hay nada que pisar.
const recalcularLineas = async (pedidoId, orgId) => {
  const { data: items, error } = await supabase
    .from('jugadores_prendas')
    .select('id, prenda_id, talla, cantidad')
    .eq('pedido_id', pedidoId)
    .eq('org_id', orgId)
  if (error) throw error

  const porLlave = new Map()
  ;(items || []).forEach((it) => {
    const key = `${it.prenda_id}__${it.talla}`
    if (!porLlave.has(key)) porLlave.set(key, { prenda_id: it.prenda_id, talla: it.talla, cantidad: 0, ids: [] })
    const g = porLlave.get(key)
    g.cantidad += it.cantidad || 1
    g.ids.push(it.id)
  })

  await supabase.from('pedidos_uniforme_lineas').delete().eq('pedido_id', pedidoId).eq('org_id', orgId)
  if (porLlave.size === 0) return

  const { data: nuevas, error: insErr } = await supabase
    .from('pedidos_uniforme_lineas')
    .insert([...porLlave.values()].map((g) => ({
      org_id: orgId, pedido_id: pedidoId, prenda_id: g.prenda_id,
      talla: g.talla, cantidad_pedida: g.cantidad, cantidad_recibida: 0,
    })))
    .select()
  if (insErr) throw insErr

  for (const linea of nuevas || []) {
    const grupo = porLlave.get(`${linea.prenda_id}__${linea.talla}`)
    if (!grupo) continue
    const { error: upErr } = await supabase
      .from('jugadores_prendas')
      .update({ linea_id: linea.id })
      .in('id', grupo.ids)
      .eq('org_id', orgId)
    if (upErr) throw upErr
  }
}

const pedidoEnBorrador = async (pedidoId, orgId) => {
  const { data, error } = await supabase
    .from('pedidos_uniforme').select('id, estado').eq('id', pedidoId).eq('org_id', orgId).single()
  if (error || !data) throw new Error('El pedido no existe o no pertenece a esta organizacion')
  if (data.estado !== 'borrador') {
    throw new Error('Solo se puede modificar un pedido en borrador')
  }
  return data
}

// Aplica la regla del pedido a sus piezas: cada una toma la fecha de pago del pedido.
// Si su cargo existe se le fija; si no, se le crea; y la pieza queda enlazada a el.
//
// Se corre DESPUES de asignar pedido_id, para leer todo de una sola vez. Es
// idempotente: volver a correrla sobre el mismo pedido no duplica cargos, porque la
// segunda vez las piezas ya traen cargo_id y caen en la rama "ya tiene cargo".
export const aplicarCobroAlPedido = async (pedidoId, orgId) => {
  const vacio = { fijados: 0, creados: 0, enlazados: 0, cubiertasPorPlan: 0, sinCobro: 0 }

  const { data: pedido, error: pedErr } = await supabase
    .from('pedidos_uniforme')
    .select('id, fecha_pago')
    .eq('id', pedidoId).eq('org_id', orgId).single()
  if (pedErr) throw pedErr
  if (!pedido?.fecha_pago) return vacio

  let piezasQuery = supabase
    .from('jugadores_prendas')
    .select('id, jugador_id, prenda_id, temporada_id, cantidad, cargo_id')
    .eq('pedido_id', pedidoId)
  piezasQuery = applyOrgFilter(piezasQuery, orgId)
  const { data: piezas, error: pzErr } = await piezasQuery
  if (pzErr) throw pzErr
  if (!piezas?.length) return vacio

  const prendaIds = [...new Set(piezas.map((p) => p.prenda_id))]
  const jugadorIds = [...new Set(piezas.map((p) => p.jugador_id))]
  const temporadaIds = [...new Set(piezas.map((p) => p.temporada_id))]

  const { data: prendasData } = await supabase
    .from('prendas').select('id, concepto_id, precio, nombre').in('id', prendaIds)
  const prendaById = Object.fromEntries((prendasData || []).map((p) => [p.id, p]))
  const conceptoIds = [...new Set((prendasData || []).map((p) => p.concepto_id).filter(Boolean))]

  let cargos = []
  if (conceptoIds.length) {
    let cargosQuery = supabase
      .from('pagos_cargos')
      .select('id, jugador_id, concepto_id, temporada_id, fecha_vencimiento')
      .in('jugador_id', jugadorIds)
      .in('concepto_id', conceptoIds)
      .in('temporada_id', temporadaIds)
      .neq('estatus', 'anulado')
    cargosQuery = applyOrgFilter(cargosQuery, orgId)
    const { data } = await cargosQuery
    cargos = data || []
  }

  // Que conceptos ya van dentro del plan de cada jugador. Sin esto, una prenda
  // disuelta en el plan se cobraria DOS veces.
  const conceptosEnPlan = new Set()
  if (conceptoIds.length) {
    const { data: asigs } = await supabase
      .from('pagos_jugador_plan')
      .select('jugador_id, plan_id')
      .in('jugador_id', jugadorIds)
      .in('temporada_id', temporadaIds)
      .eq('activo', true)
    const planIds = [...new Set((asigs || []).map((a) => a.plan_id))]
    if (planIds.length) {
      const { data: items } = await supabase
        .from('pagos_planes_items')
        .select('plan_id, concepto_id')
        .in('plan_id', planIds)
        .in('concepto_id', conceptoIds)
      const conceptosDelPlan = new Map()
      ;(items || []).forEach((it) => {
        if (!conceptosDelPlan.has(it.plan_id)) conceptosDelPlan.set(it.plan_id, [])
        conceptosDelPlan.get(it.plan_id).push(it.concepto_id)
      })
      ;(asigs || []).forEach((a) => {
        (conceptosDelPlan.get(a.plan_id) || []).forEach((cid) => {
          conceptosEnPlan.add(`${a.jugador_id}|${cid}`)
        })
      })
    }
  }

  const plan = planearCobro({ piezas, prendaById, cargos, conceptosEnPlan, fecha: pedido.fecha_pago })

  for (const f of plan.fijar) {
    let q = supabase.from('pagos_cargos').update({ fecha_vencimiento: f.fecha }).eq('id', f.cargo_id)
    q = applyOrgFilter(q, orgId)
    const { error } = await q
    if (error) throw error
  }

  for (const c of plan.crear) {
    const prenda = Object.values(prendaById).find((p) => p.concepto_id === c.concepto_id)
    const { data: cargo, error } = await supabase
      .from('pagos_cargos')
      .insert({
        org_id: orgId,
        temporada_id: c.temporada_id,
        jugador_id: c.jugador_id,
        concepto_id: c.concepto_id,
        monto_original: c.monto,
        monto_final: c.monto,
        monto_pagado: 0,
        saldo: c.monto,
        estatus: 'pendiente',
        fecha_vencimiento: c.fecha,
        notas: `${prenda?.nombre || 'Uniforme'} (x${c.cantidad}) - pedido`,
      })
      .select('id').single()
    if (error) throw error
    let upd = supabase.from('jugadores_prendas').update({ cargo_id: cargo.id }).in('id', c.pieza_ids)
    upd = applyOrgFilter(upd, orgId)
    const { error: upErr } = await upd
    if (upErr) throw upErr
  }

  for (const e of plan.enlazar) {
    let q = supabase.from('jugadores_prendas').update({ cargo_id: e.cargo_id }).eq('id', e.pieza_id)
    q = applyOrgFilter(q, orgId)
    const { error } = await q
    if (error) throw error
  }

  return {
    fijados: plan.fijar.length,
    creados: plan.crear.length,
    enlazados: plan.enlazar.length,
    cubiertasPorPlan: plan.cubiertasPorPlan.length,
    sinCobro: plan.sinCobro.length,
  }
}

export const crearPedido = async (
  { proveedor, fecha = null, fechaPago = null, notas = null, temporadaId = null, deporte = null },
  jugadorPrendaIds = [],
) => {
  try {
    if (!proveedor || !proveedor.trim()) return { data: null, error: new Error('El proveedor es obligatorio') }
    if (!temporadaId) return { data: null, error: new Error('La temporada es obligatoria') }
    // Un borrador VACIO es valido: el pedido se ARMA, se le van agregando piezas
    // conforme llegan las tallas. Exigir prendas aqui dejaba sin salida al club que
    // todavia no ha capturado ninguna. El candado vive en marcarPedidoListo, que es
    // lo que ve el proveedor.

    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const insert = {
      org_id: orgId, proveedor: proveedor.trim(), created_by: staff?.id || null,
      estado: 'borrador', temporada_id: temporadaId, deporte: deporte || null,
      fecha_pago: fechaPago || null,
    }
    if (fecha) insert.fecha_pedido = fecha
    if (notas) insert.notas = notas

    const { data: pedido, error } = await supabase
      .from('pedidos_uniforme').insert(insert).select().single()
    if (error) throw error

    const { error: upErr } = await supabase
      .from('jugadores_prendas').update({ pedido_id: pedido.id })
      .in('id', jugadorPrendaIds).eq('org_id', orgId)
    if (upErr) throw upErr

    await recalcularLineas(pedido.id, orgId)
    const cobro = await aplicarCobroAlPedido(pedido.id, orgId)
    return { data: pedido, cobro, error: null }
  } catch (error) {
    console.error('[crearPedido]', error)
    return { data: null, error }
  }
}

export const agregarAPedido = async (pedidoId, jugadorPrendaIds = []) => {
  try {
    if (!jugadorPrendaIds.length) return { error: null }
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
    await pedidoEnBorrador(pedidoId, orgId)
    const { error } = await supabase
      .from('jugadores_prendas').update({ pedido_id: pedidoId })
      .in('id', jugadorPrendaIds).eq('org_id', orgId)
    if (error) throw error
    await recalcularLineas(pedidoId, orgId)
    // La pieza que llega tarde toma la fecha del pedido al que entra, no una nueva.
    const cobro = await aplicarCobroAlPedido(pedidoId, orgId)
    return { cobro, error: null }
  } catch (error) {
    console.error('[agregarAPedido]', error)
    return { error }
  }
}

export const quitarDePedido = async (jugadorPrendaIds = []) => {
  try {
    if (!jugadorPrendaIds.length) return { error: null }
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data: filas, error: selErr } = await supabase
      .from('jugadores_prendas').select('id, pedido_id').in('id', jugadorPrendaIds).eq('org_id', orgId)
    if (selErr) throw selErr
    const pedidoIds = [...new Set((filas || []).map((f) => f.pedido_id).filter(Boolean))]
    for (const pid of pedidoIds) await pedidoEnBorrador(pid, orgId)

    const { error } = await supabase
      .from('jugadores_prendas').update({ pedido_id: null, linea_id: null })
      .in('id', jugadorPrendaIds).eq('org_id', orgId)
    if (error) throw error
    for (const pid of pedidoIds) await recalcularLineas(pid, orgId)
    return { error: null }
  } catch (error) {
    console.error('[quitarDePedido]', error)
    return { error }
  }
}

export const actualizarPedido = async (pedidoId, { proveedor, fecha_pedido, fecha_pago, notas } = {}) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
    await pedidoEnBorrador(pedidoId, orgId)
    const updates = {}
    if (proveedor !== undefined) {
      if (!proveedor.trim()) return { error: new Error('El proveedor es obligatorio') }
      updates.proveedor = proveedor.trim()
    }
    if (fecha_pedido !== undefined) updates.fecha_pedido = fecha_pedido
    if (fecha_pago !== undefined) updates.fecha_pago = fecha_pago || null
    if (notas !== undefined) updates.notas = notas
    if (!Object.keys(updates).length) return { error: null }

    const { error } = await supabase
      .from('pedidos_uniforme').update(updates).eq('id', pedidoId).eq('org_id', orgId)
    if (error) throw error
    // Poner (o corregir) la fecha del pedido alcanza a las piezas que ya tiene.
    if (fecha_pago !== undefined) await aplicarCobroAlPedido(pedidoId, orgId)
    return { error: null }
  } catch (error) {
    console.error('[actualizarPedido]', error)
    return { error }
  }
}

// Recepcion de prenda GENERICA: llega cantidad, no llegan nombres.
export const recibirLinea = async (lineaId, cantidad) => {
  try {
    const n = Number(cantidad)
    if (!Number.isFinite(n) || n < 0) return { error: new Error('La cantidad no puede ser negativa') }
    if (!Number.isInteger(n)) return { error: new Error('La cantidad debe ser un numero entero') }
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data: linea, error: selErr } = await supabase
      .from('pedidos_uniforme_lineas')
      .select('id, cantidad_pedida')
      .eq('id', lineaId)
      .eq('org_id', orgId)
      .single()
    if (selErr || !linea) return { error: new Error('La linea no existe o no es de esta organizacion') }
    if (n > linea.cantidad_pedida) {
      return { error: new Error(`No puedes recibir mas de lo pedido (${linea.cantidad_pedida})`) }
    }

    const { error } = await supabase
      .from('pedidos_uniforme_lineas')
      .update({ cantidad_recibida: n, updated_at: new Date().toISOString() })
      .eq('id', lineaId).eq('org_id', orgId)
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[recibirLinea]', error)
    return { error }
  }
}

// Recepcion de prenda PERSONALIZADA: llegan piezas a nombre de alguien. La
// cantidad de la linea se recalcula desde los items, no se captura a mano.
export const recibirItems = async (jugadorPrendaIds = [], recibido = true) => {
  try {
    if (!jugadorPrendaIds.length) return { error: null }
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { error } = await supabase
      .from('jugadores_prendas')
      .update({ recibido_at: recibido ? new Date().toISOString() : null })
      .in('id', jugadorPrendaIds).eq('org_id', orgId)
    if (error) throw error

    const { data: filas } = await supabase
      .from('jugadores_prendas').select('linea_id').in('id', jugadorPrendaIds).eq('org_id', orgId)
    const lineaIds = [...new Set((filas || []).map((f) => f.linea_id).filter(Boolean))]

    for (const lid of lineaIds) {
      const { data: items } = await supabase
        .from('jugadores_prendas').select('cantidad, recibido_at').eq('linea_id', lid).eq('org_id', orgId)
      const recibidas = (items || [])
        .filter((i) => i.recibido_at)
        .reduce((s, i) => s + (i.cantidad || 1), 0)
      const { error: upLineaErr } = await supabase
        .from('pedidos_uniforme_lineas')
        .update({ cantidad_recibida: recibidas, updated_at: new Date().toISOString() })
        .eq('id', lid).eq('org_id', orgId)
      if (upLineaErr) throw upLineaErr
    }
    return { error: null }
  } catch (error) {
    console.error('[recibirItems]', error)
    return { error }
  }
}

// `guard` corre DESPUES de validar la transicion y ANTES de escribirla: asi un
// pedido ya enviado dice "solo desde borrador" y no "esta vacio", que seria
// confundir el sintoma con la causa.
const cambiarEstadoPedido = async (pedidoId, desde, hacia, extra = {}, guard = null) => {
  const { data: staff } = (await getCurrentStaff()) || {}
  const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
  const { data: actual, error: selErr } = await supabase
    .from('pedidos_uniforme').select('id, estado').eq('id', pedidoId).eq('org_id', orgId).single()
  if (selErr || !actual) throw new Error('El pedido no existe o no pertenece a esta organizacion')
  if (!desde.includes(actual.estado)) {
    throw new Error(`Un pedido en "${actual.estado}" solo puede pasar a "${hacia}" desde ${desde.join(' o ')}`)
  }
  if (guard) await guard(orgId)
  const { error } = await supabase
    .from('pedidos_uniforme').update({ estado: hacia, ...extra })
    .eq('id', pedidoId).eq('org_id', orgId)
  if (error) throw error
}

// Congela lo que va bordado. Se hace al marcar listo, que es cuando el pedido
// deja de editarse: a partir de ahi, cambiar el numero de un jugador en su grupo
// ya no puede reescribir lo que el proveedor esta produciendo.
// Solo toca las personalizadas, y solo las que nadie congelo a mano.
const sellarImpresos = async (pedidoId, orgId) => {
  const { data: items, error } = await supabase
    .from('jugadores_prendas')
    .select(`
      id, grupo_id, numero_impreso, nickname_impreso,
      prendas(personalizada),
      jugadores(numero, nickname, jugadores_grupos(grupo_id, numero_jersey, nickname))
    `)
    .eq('pedido_id', pedidoId)
    .eq('org_id', orgId)
  if (error) throw error

  for (const it of items || []) {
    if (it.prendas?.personalizada !== true) continue
    const updates = {}
    if (it.numero_impreso == null || it.numero_impreso === '') {
      const n = numeroEfectivo(it, it.jugadores || {})
      updates.numero_impreso = n != null ? String(n) : null
    }
    if (it.nickname_impreso == null || it.nickname_impreso === '') {
      const nk = nicknameEfectivo(it, it.jugadores || {})
      updates.nickname_impreso = nk != null ? String(nk) : null
    }
    if (!Object.keys(updates).length) continue
    const { error: upErr } = await supabase
      .from('jugadores_prendas').update(updates).eq('id', it.id).eq('org_id', orgId)
    if (upErr) throw upErr
  }
}

export const marcarPedidoListo = async (pedidoId) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    // Aqui vive el candado que antes estaba en crearPedido: un borrador puede estar
    // vacio mientras se arma, pero "listo" es lo que se le manda al proveedor.
    // Va como guard para que corra despues de validar la transicion.
    await cambiarEstadoPedido(
      pedidoId, ['borrador'], 'listo', { listo_at: new Date().toISOString() },
      async (orgActiva) => {
        const { data: piezas } = await supabase
          .from('jugadores_prendas').select('id')
          .eq('pedido_id', pedidoId).eq('org_id', orgActiva)
        if (!piezas || !piezas.length) {
          throw new Error('El pedido esta vacio. Agregale prendas antes de marcarlo listo.')
        }
      },
    )
    await sellarImpresos(pedidoId, orgId)
    return { error: null }
  } catch (error) {
    console.error('[marcarPedidoListo]', error)
    return { error }
  }
}

// Edita lo que va bordado en UNA prenda. Solo mientras el pedido es borrador:
// despues, lo bordado es un hecho y no se reescribe desde la app.
export const actualizarImpresoDePrenda = async (jugadorPrendaId, { numero_impreso, nickname_impreso } = {}) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

    const { data: item, error: selErr } = await supabase
      .from('jugadores_prendas')
      .select('id, pedido_id')
      .eq('id', jugadorPrendaId)
      .eq('org_id', orgId)
      .single()
    if (selErr || !item) throw new Error('La prenda no existe o no pertenece a esta organizacion')
    if (item.pedido_id) await pedidoEnBorrador(item.pedido_id, orgId)

    const updates = {}
    if (numero_impreso !== undefined) updates.numero_impreso = numero_impreso?.trim() || null
    if (nickname_impreso !== undefined) updates.nickname_impreso = nickname_impreso?.trim() || null
    if (!Object.keys(updates).length) return { error: null }

    const { error } = await supabase
      .from('jugadores_prendas').update(updates).eq('id', jugadorPrendaId).eq('org_id', orgId)
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('[actualizarImpresoDePrenda]', error)
    return { error }
  }
}

export const regresarPedidoABorrador = async (pedidoId) => {
  try {
    await cambiarEstadoPedido(pedidoId, ['listo'], 'borrador', { listo_at: null })
    return { error: null }
  } catch (error) {
    console.error('[regresarPedidoABorrador]', error)
    return { error }
  }
}

export const marcarPedidoEnviado = async (pedidoId, fecha = null) => {
  try {
    const enviadoAt = fecha ? new Date(`${fecha}T12:00:00`).toISOString() : new Date().toISOString()
    await cambiarEstadoPedido(pedidoId, ['listo'], 'enviado', { enviado_at: enviadoAt })
    return { error: null }
  } catch (error) {
    console.error('[marcarPedidoEnviado]', error)
    return { error }
  }
}

// Conserva cabecera, lineas e items: son la evidencia de que se pidio. Las prendas
// vuelven solas a "Por pedir" porque etapaUniforme ignora los pedidos cancelados.
export const cancelarPedido = async (pedidoId) => {
  try {
    await cambiarEstadoPedido(pedidoId, ['borrador', 'listo', 'enviado'], 'cancelado', {
      cancelado_at: new Date().toISOString(),
    })
    return { error: null }
  } catch (error) {
    console.error('[cancelarPedido]', error)
    return { error }
  }
}
